import React, { useState, useEffect } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { SettingsCategory } from '../../config/SettingsCategory';
import { useSettingsPage } from '../../hooks/useSettingsPage';
import { KEYBOARD_SETTING_IDS } from '../../config/SettingIds';
import { KeyboardInputModeSettingsPage } from '../KeyboardInputModeSettings/KeyboardInputModeSettingsPage';
import { SettingsRenderer } from '../../SettingsProvider/SettingsRenderer';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { NativePlatform } from '../../Contracts/base';

// Navigation views for Keyboard Settings
export enum KeyboardSettingsView {
	Keyboard = 'keyboard',
	KeyboardInputMode = 'keyboard-input-mode',
}

interface KeyboardSettingsPageProps {
	title: string;
	description?: string;
}

export const KeyboardSettingsPage: React.FC<KeyboardSettingsPageProps> = ({ title }) => {
	const [currentView, setCurrentView] = useState<KeyboardSettingsView>(
		KeyboardSettingsView.Keyboard
	);
	const [platform, setPlatform] = useState<string>('windows');

	const { settingsState, availableSettingIds, handleChangeBySettingId, restoreDefaults } =
		useSettingsPage({
			category: SettingsCategory.Keyboard,
			defaultValue: true,
		});

	useEffect(() => {
		const bridge = SettingsNativeBridgeFactory.getBridge();
		setPlatform(bridge.getPlatform());
	}, []);

	const navigateToKeyboardInputModeView = () => {
		setCurrentView(KeyboardSettingsView.KeyboardInputMode);
	};

	const navigateToKeyboardView = () => {
		setCurrentView(KeyboardSettingsView.Keyboard);
	};

	// Navigation handlers map
	const navigationHandlers = {
		[KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE]: navigateToKeyboardInputModeView,
	};

	// Handle sub-page navigation
	if (currentView === KeyboardSettingsView.KeyboardInputMode) {
		return (
			<KeyboardInputModeSettingsPage
				title="Keyboard input mode"
				onNavigatingBack={navigateToKeyboardView}
			/>
		);
	}

	return (
		<SettingsPage
			title={title}
			showRestoreDefaults={platform === NativePlatform.MAC}
			onRestoreDefaults={restoreDefaults}
			settings={
				<>
					{/* Dynamically render all available settings */}
					{availableSettingIds.map((settingId) => (
						<SettingsRenderer
							key={settingId}
							settingId={settingId}
							settingsState={settingsState}
							handleChange={handleChangeBySettingId}
							navigationHandlers={navigationHandlers}
						/>
					))}
				</>
			}
		/>
	);
};
